import { deepCompareEQ } from '@cf-shared/deep-helpers';
import { FitnessPlan } from '@cf-shared/data-model/fitness-plan';
import { IntakeFormState } from '@cf-shared/data-model/intake-form-state';

/**
 * #4660: which program the "Change program" flow persists when the user presses Finish.
 *
 * The Intake page keeps the newly built program in two places. One is the field
 * `generatedFitnessPlan`. The other is inside the snapshot
 * `generatedPlanIntakeFormState`, which is also what the page journals to
 * `log/<ts>-IntakeForm` at finish. In a healthy run the two hold the same program.
 *
 * They can come apart. The page's document watcher re-points the FIELD at whatever
 * program an arriving `intakeform` document carries, and used to leave the snapshot
 * alone. A user then finished with the two disagreeing, and the save used the field —
 * which is how a rebuilt and hand-edited program was replaced by the old one, with the
 * journal beside it recording the program the user actually meant. Nothing reported it.
 *
 * So the save reads the snapshot: it is the copy every legitimate plan change refreshes,
 * and the copy the journal already records, which means the journal and the write can no
 * longer describe different programs.
 */
export interface FinishPlanChoice {
  /** the program to persist */
  plan: FitnessPlan | undefined;
  /** true when the page's two copies held different programs — a condition to report */
  copiesDisagreed: boolean;
}

/**
 * @param trackedPlan the page's `generatedFitnessPlan` field
 * @param restoredState `intakeFormState` after it has been restored from the snapshot,
 *   which is the state the intake journal is written from
 */
export function choosePlanToPersistAtFinish(
  trackedPlan: FitnessPlan | undefined,
  restoredState: IntakeFormState | undefined,
): FinishPlanChoice {
  const snapshotPlan = restoredState?.generatedFitnessPlan;
  // no snapshot program is not a disagreement — there is nothing to disagree with, and
  // refusing to save here would cost the user their plan for a reason they cannot act on
  if (!snapshotPlan) {
    return { plan: trackedPlan, copiesDisagreed: false };
  }
  // a field holding no program at all was CLEARED on purpose, not drifted: somethingChanged()
  // empties it when an answer change invalidates the plan. Persisting the snapshot's plan here
  // would put back a program the user's own edit had just thrown away, so this answers with the
  // empty field — which the caller passes on, and the write path then does nothing, exactly as
  // it did before this rule existed. It is also why deepCompareEQ, which takes two objects, is
  // never reached with an empty one.
  if (!trackedPlan) {
    return { plan: trackedPlan, copiesDisagreed: false };
  }
  // compared by CONTENT, not by object identity: every arriving document replaces the
  // snapshot's program with a fresh deep copy, so identity differs on every healthy sync
  return {
    plan: snapshotPlan,
    copiesDisagreed: !deepCompareEQ(trackedPlan, snapshotPlan),
  };
}

/**
 * The few facts about a program that let a reader of a disagreement report tell which of
 * the two copies was which, without putting a whole plan into the telemetry payload.
 */
export function describePlanForReport(plan: FitnessPlan | undefined) {
  return {
    startPoint_utc: plan?.startPoint?.timestamp_utc ?? null,
    workouts: plan?.workouts?.length ?? 0,
    subSlots: Object.keys(plan?.subSlots ?? {}).length,
  };
}
